import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";

/**
 * Global provider that fetches every published text override from
 * /api/texts once at app-start and exposes a resolver so any component
 * can render the CMS-managed copy for a given slot key + language.
 *
 * Usage:
 *   <CmsText slotKey="home.hero.title" es="..." en="..." pt="..." />
 *   const { t } = useTexts(); t("home.hero.title", { es, en, pt })
 */
const TextsCtx = createContext({
  map: {},
  ready: false,
  t: (_k, defaults) => defaults,
  reload: () => {},
});

export function TextsProvider({ children }) {
  const [map, setMap] = useState({});
  const [ready, setReady] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await api.get("/texts");
      setMap(r.data?.texts || {});
    } catch (e) {
      // silent — CmsText renders the default copy passed as props
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const t = useCallback((key, defaults = {}) => {
    if (!key || !map[key]) return defaults;
    // Fill in fallback for any language the CMS omitted
    return {
      es: map[key].es || defaults.es,
      en: map[key].en || defaults.en,
      pt: map[key].pt || defaults.pt,
    };
  }, [map]);

  return (
    <TextsCtx.Provider value={{ map, ready, t, reload }}>
      {children}
    </TextsCtx.Provider>
  );
}

export function useTexts() {
  return useContext(TextsCtx);
}

/**
 * Renders CMS-managed HTML text for the active language. If the slotKey has
 * no override, the built-in defaults (es/en/pt props) are used.
 *
 * The text is treated as light HTML (backend sanitizer only allows b/i/u/br
 * tags — see server.py _sanitize_html). Everything else is escaped/stripped.
 */
export function CmsText({ slotKey, es = "", en = "", pt = "", lang, as = "span", className = "", style, ...rest }) {
  const { t } = useTexts();
  const resolved = t(slotKey, { es, en, pt });
  const value = resolved[lang] || resolved.es || "";
  const Tag = as;
  return (
    <Tag
      className={className}
      style={style}
      dangerouslySetInnerHTML={{ __html: value }}
      {...rest}
    />
  );
}
